import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * PortfolioEmptyState — §0.5's whole-dashboard first-run panel. Zero Property rows → this renders in
 * `.content`'s place INSTEAD OF the KPI row/rent-roll/WhatsApp list/leases-expiring/units grid in
 * "populated-but-zero" ghost form (an all-dash KPI row strands a new tenant with no path forward).
 *
 * Pattern: exact match to the established zero-state idiom, AccountsGrid.tsx:47,70
 * (`accounts-empty-create`, `min-h-[44px] rounded-lg bg-primary text-white text-sm font-semibold`).
 *
 * The CTA opens a minimal inline create form — §0.3's model (name + optional address + owner
 * Company), not §0.6's bulk-import wedge (a separate, bigger onboarding flow nobody has assigned a
 * UI for yet) — so the button is never wired to a no-op. POSTs to the already-built
 * POST /api/properties; on success the parent refetches and the real dashboard takes over.
 */
export const PortfolioEmptyState: React.FC<{ onCreated: () => void; onImport?: () => void }> = ({ onCreated, onImport }) => {
  const { t } = useTranslation('common');
  const [creating, setCreating] = useState(false);

  if (!creating) {
    return (
      <div className="flex flex-col items-center gap-3 py-16 text-center" data-testid="portfolio-empty">
        <div className="text-sm text-text-muted">{t('portfolio.empty.title')}</div>
        <button type="button" data-testid="portfolio-empty-create" onClick={() => setCreating(true)}
          className="min-h-[44px] px-4 rounded-lg bg-primary text-white text-sm font-semibold">
          {t('portfolio.empty.cta')}
        </button>
        {/* The onboarding wedge, offered right at the dead-end the operator hit: skip the one-field
            create and load a whole building (units + tenants) in one go (brief §1). */}
        {onImport && (
          <button type="button" data-testid="portfolio-empty-import" onClick={onImport}
            className="min-h-[44px] px-4 rounded-lg border border-primary/40 text-sm font-semibold text-primary hover:bg-primary/5">
            {t('portfolio.setup.importBuilding')}
          </button>
        )}
      </div>
    );
  }
  return <PropertyCreateForm onCreated={onCreated} onCancel={() => setCreating(false)} />;
};

interface Company { id: string; name: string }

const PropertyCreateForm: React.FC<{ onCreated: () => void; onCancel: () => void }> = ({ onCreated, onCancel }) => {
  const { t } = useTranslation('common');
  const [companies, setCompanies] = useState<Company[] | null>(null);
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    fetch('/api/companies')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((rows: any[]) => { if (alive) setCompanies(rows.map((c) => ({ id: c.id, name: c.name || '—' }))); })
      .catch(() => { if (alive) setCompanies([]); });
    return () => { alive = false; };
  }, []);

  const canSubmit = name.trim() && companyId;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch('/api/properties', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companyId, name: name.trim(), address: address.trim() || null }),
      });
      if (!res.ok) { setError(t('portfolio.empty.create.error')); return; }
      onCreated();
    } catch {
      setError(t('portfolio.empty.create.error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-md mx-auto py-10" data-testid="portfolio-property-create-form">
      <h2 className="text-lg font-bold text-text-strong mb-4">{t('portfolio.empty.create.title')}</h2>
      {companies !== null && companies.length === 0 ? (
        <div className="flex flex-col gap-3">
          <div className="text-sm text-text-muted">{t('portfolio.empty.create.noCompanies')}</div>
          <button type="button" onClick={onCancel} data-testid="portfolio-property-create-cancel"
            className="min-h-[44px] px-4 rounded-lg border border-border-neutral text-sm font-semibold text-text-strong self-start">
            {t('portfolio.empty.create.cancel')}
          </button>
        </div>
      ) : (
        <form onSubmit={submit} className="flex flex-col gap-3">
          <label className="text-xs text-text-muted flex flex-col gap-1">
            {t('portfolio.empty.create.name')}
            <input autoFocus required data-testid="portfolio-property-create-name" value={name} onChange={(e) => setName(e.target.value)}
              className="h-10 px-3 rounded-lg border border-border-neutral text-sm font-semibold" dir="auto" />
          </label>
          <label className="text-xs text-text-muted flex flex-col gap-1">
            {t('portfolio.empty.create.owner')}
            <select required data-testid="portfolio-property-create-owner" value={companyId} onChange={(e) => setCompanyId(e.target.value)}
              className="h-10 px-3 rounded-lg border border-border-neutral text-sm bg-surface">
              <option value="" disabled>{t('portfolio.empty.create.ownerPlaceholder')}</option>
              {(companies ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <label className="text-xs text-text-muted flex flex-col gap-1">
            {t('portfolio.empty.create.address')}
            <input data-testid="portfolio-property-create-address" value={address} onChange={(e) => setAddress(e.target.value)}
              className="h-10 px-3 rounded-lg border border-border-neutral text-sm" dir="auto" />
          </label>
          {!canSubmit && (name || companyId) && (
            <div className="text-xs text-opt-rose" data-testid="portfolio-property-create-requirement">{t('portfolio.empty.create.requirement')}</div>
          )}
          {error && <div className="text-xs text-opt-rose" data-testid="portfolio-property-create-error">{error}</div>}
          <div className="flex items-center gap-2 mt-1">
            <button type="submit" disabled={!canSubmit || submitting} data-testid="portfolio-property-create-submit"
              className="h-10 px-4 rounded-lg bg-primary text-white text-sm font-semibold disabled:opacity-50">
              {t('portfolio.empty.create.submit')}
            </button>
            <button type="button" onClick={onCancel} data-testid="portfolio-property-create-cancel"
              className="h-10 px-4 rounded-lg text-sm font-semibold text-text-muted">
              {t('portfolio.empty.create.cancel')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};
